import {
  ReactNode,
  ChangeEvent,
  useReducer,
  useState,
  SyntheticEvent,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import Paginator from "@/components/Paginator";
import ColumnHeader from "@/components/ColumnHeader";
import AdvanceFilter from "@/components/AdvanceFilter";
import Drawer from "@/components/Drawer";
import { useOrdersContext } from "@/contexts/orders-context";
import {
  Tabs,
  TabPane,
  Table,
  Modal,
  Input,
  Button,
  Tooltip,
  Checkbox,
  Icon,
} from "@/vendor/Forge/dist";
import { ProcedureTable, RowData } from "@/types/my-tasks";
import { ProceduresQueryKey } from "@/types/procedure";
import { Filter, Tabs as TabsType } from "@/types/order-table";
import CaseDetails from "@/components/CaseDetails";
import Notes from "@/components/Notes";
import { authStatusMapping } from "@/utils/mappers";
import { withinLast24Hours } from "@/utils/dateConversions";
import {
  getNewSortOrder,
  getSortOrder,
  renderPatientName,
  renderInsurance,
  isValidAuthStatus,
  renderSurgeryDate,
} from "./helpers";
import {
  Wrapper,
  PaginatorWrapper,
  SearchInteractionsContainer,
  SearchWrapper,
  TableWrapper,
  RefetchContainer,
  LastUpdatedContainer,
  Spacer,
} from "./components.stitches";
import { formatDate, formatMillisDate } from "@/utils/helpers";
import { DateTime } from "luxon";
import BulkStatusUpdate from "./BulkStatusUpdate";

interface ModalType {
  show: boolean;
  useLarge: boolean;
  headerText: string;
  renderForm: () => React.ReactNode;
}

interface Props {
  totalItems: number;
  rows: ProcedureTable[];
  tabs: TabsType[];
  excludedColumns?: string[];
  ordersQueryKey: ProceduresQueryKey;
  totalPages: number;
  table: {
    currentTable: string;
    setCurrentTable: Dispatch<SetStateAction<string>>;
  };
  refetch: () => void;
  dataUpdatedAt: number;
}

const OrderTable = ({
  totalItems,
  rows,
  tabs,
  excludedColumns = [],
  totalPages,
  table: { currentTable, setCurrentTable },
  refetch,
  dataUpdatedAt,
}: Props) => {
  const {
    filters,
    pageNumber,
    setPageNumber,
    updateFilters,
    sortOrder,
    sortCriteria,
    setSortOrder,
    setSortCriteria,
  } = useOrdersContext();

  const [modal, setModal] = useReducer(
    (state: ModalType, changes: Partial<ModalType>) => ({
      ...state,
      ...changes,
    }),
    {
      show: false,
      useLarge: false,
      headerText: "",
      renderForm: () => null,
    },
  );
  const [patientName, setPatientName] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [rowState, updateRows] = useState<ProcedureTable[]>(rows);
  const [drawerData, setDrawerData] = useState<ProcedureTable>(rowState[0]);
  const [ordersToUpdateStatus, setOrdersToUpdateStatus] = useState<string[]>(
    [],
  );

  useEffect(() => {
    updateRows((prev) =>
      prev.map((proc) => {
        return {
          ...proc,
          checked: ordersToUpdateStatus.includes(proc.caseNumber),
        };
      }),
    );
  }, [ordersToUpdateStatus]);

  useEffect(() => {
    if (filters.recentlyUpdated?.length) {
      updateRows((rows) =>
        rows.filter((row: ProcedureTable) => {
          return withinLast24Hours(row.lastUpdated);
        }),
      );
    } else {
      updateRows(rows);
    }
  }, [filters, rows]);

  const handleTabChange = (
    e:
      | ChangeEvent<HTMLSelectElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>,
    selectedTab: number,
  ) => {
    let authStatuses: string[] = [];

    switch (tabs[selectedTab].tabLabel) {
      case "Completed":
        authStatuses = ["Closed", "Not Required", "Obtained"];
        break;
      case "All":
        authStatuses = [
          "Closed",
          "Not Required",
          "Obtained",
          "Needs Info",
          "Pending",
        ];
        break;
      default:
        authStatuses = [tabs[selectedTab].tabLabel];
    }

    updateFilters("authStatus", authStatuses);
    setCurrentTable(tabs[selectedTab].tabLabel);
    setPageNumber(1);
  };

  const handleFilterClick = ({
    type,
    header,
    currentTable,
  }: {
    type: Filter;
    header: string;
    currentTable?: string;
  }) => {
    setModal({
      show: true,
      useLarge: true,
      headerText: `Filter by ${header}`,
      renderForm: () => (
        <AdvanceFilter
          closeModal={() => setModal({ show: false })}
          type={type}
          currentTable={currentTable}
        />
      ),
    });
  };

  const renderProcedure = (
    scheduledProcedureName: string,
    updatedProcedureName: string | null,
  ) => {
    return updatedProcedureName ? (
      updatedProcedureName
    ) : (
      <Tooltip
        id={`tool-procedure-${scheduledProcedureName}`}
        text="Order is currently displaying scheduled procedure name. If needed you can provide an updated procedure name in Case Details."
      >
        {scheduledProcedureName}
      </Tooltip>
    );
  };

  const handleRecentlyUpdatedHighlight = (
    _text: string,
    { rowData: { lastUpdated } }: RowData<ProcedureTable>,
  ) => {
    return {
      className: withinLast24Hours(lastUpdated) ? "HighlightCell" : "",
    };
  };

  const excludedAuthFilterStatuses = ["Needs Info", "Pending"];

  const handleAddToBulkUpdate = (
    checked: boolean,
    procedure: ProcedureTable,
  ) => {
    if (checked) {
      setOrdersToUpdateStatus((prev) => [...prev, procedure.caseNumber]);
    } else {
      setOrdersToUpdateStatus((prev) =>
        prev.filter((order) => order !== procedure.caseNumber),
      );
    }
  };

  const selectAllOrders = () => {
    setOrdersToUpdateStatus(rowState.map((row) => row.caseNumber));
  };

  const clearAllOrders = () => {
    setOrdersToUpdateStatus([]);
  };

  const columns = [
    {
      key: "select",
      displayName: "",
      header: {
        formatters: [
          () => (
            <Checkbox
              data-testid="test-select-all"
              id="checkboxHeader"
              description=""
              checked={rowState.length === ordersToUpdateStatus.length}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                e.target.checked ? selectAllOrders() : clearAllOrders()
              }
            />
          ),
        ],
      },
      template: (_value: string, { rowData }: RowData<ProcedureTable>) => {
        return (
          <Checkbox
            id="checkboxHeader"
            data-testid={`test-select-${rowData.caseNumber}`}
            description=""
            checked={ordersToUpdateStatus.includes(rowData.caseNumber)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleAddToBulkUpdate(e.target.checked, rowData)
            }
          />
        );
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "surgeryDate",
      displayName: "Surgery Date",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="performDate"
              name={name}
              sortOrder={getSortOrder("performDate", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("performDate", sortCriteria, sortOrder),
                );
                setSortCriteria("performDate");
              }}
            />
          ),
        ],
      },
      template: (date: string, { rowData }: RowData<ProcedureTable>) => {
        return date ? renderSurgeryDate(rowData) : "";
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "scheduledProcedureName",
      displayName: "Procedure",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="name"
              name={name}
              sortOrder={getSortOrder("orderName", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("orderName", sortCriteria, sortOrder),
                );
                setSortCriteria("orderName");
              }}
            />
          ),
        ],
      },
      template: (
        _procedureName: string,
        {
          rowData: { updatedProcedureName, scheduledProcedureName },
        }: RowData<ProcedureTable>,
      ) => renderProcedure(scheduledProcedureName, updatedProcedureName),
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "insurance",
      displayName: "Carrier",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="insurance"
              name={name}
              sortOrder={getSortOrder("insurance", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("insurance", sortCriteria, sortOrder),
                );
                setSortCriteria("insurance");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "insurance",
                  header: "Carrier",
                })
              }
              isActiveFilter={!!filters?.insurances?.length}
            />
          ),
        ],
      },
      template: (_insurance: string, { rowData }: RowData<ProcedureTable>) =>
        renderInsurance(rowData.workingInsurances),
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "providerName",
      displayName: "Surgeon",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="provider"
              name={name}
              sortOrder={getSortOrder(
                "orderingProvider",
                sortCriteria,
                sortOrder,
              )}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("orderingProvider", sortCriteria, sortOrder),
                );
                setSortCriteria("orderingProvider");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "provider",
                  header: "Surgeon",
                })
              }
              isActiveFilter={!!filters?.providerNames?.length}
            />
          ),
        ],
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "facility",
      displayName: "Facility",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="facility"
              name={name}
              sortOrder={getSortOrder("facility", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("facility", sortCriteria, sortOrder),
                );
                setSortCriteria("facility");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "facility",
                  header: "Facility",
                })
              }
              isActiveFilter={!!filters?.facility?.length}
            />
          ),
        ],
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "athenaPatientDetails.patientName",
      displayName: "Patient",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="patient"
              name={name}
              sortOrder={getSortOrder("patientName", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("patientName", sortCriteria, sortOrder),
                );
                setSortCriteria("patientName");
              }}
              onFilterClick={() =>
                handleFilterClick({
                  type: "patientName",
                  header: "Patient Name",
                })
              }
              isActiveFilter={!!filters?.patientNames?.length}
            />
          ),
        ],
      },
      template: renderPatientName,
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "athenaPatientDetails.dob",
      displayName: "DOB",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="dob"
              name={name}
              sortOrder={getSortOrder("dob", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(getNewSortOrder("dob", sortCriteria, sortOrder));
                setSortCriteria("dob");
              }}
            />
          ),
        ],
      },
      template: (_dob: string, { rowData }: RowData<ProcedureTable>) => {
        return DateTime.fromFormat(
          rowData?.athenaPatientDetails?.dob ??
            rowData.cernerPatientDetails.dob,
          "yyyy-MM-dd",
        ).toFormat("D");
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "authStatus",
      displayName: "Auth Status",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="authStatus"
              name={name}
              onFilterClick={
                excludedAuthFilterStatuses.includes(currentTable)
                  ? undefined
                  : () =>
                      handleFilterClick({
                        type: "authStatus",
                        header: "Auth Status",
                        currentTable,
                      })
              }
              sortOrder={getSortOrder("authStatus", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("authStatus", sortCriteria, sortOrder),
                );
                setSortCriteria("authStatus");
              }}
              isActiveFilter={
                !!filters?.authStatus?.filter(isValidAuthStatus).length
              }
            />
          ),
        ],
      },
      template: (status: string) => authStatusMapping[status],
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "scheduledStatus",
      displayName: "Scheduled Status",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="scheduledStatus"
              name={name}
              onFilterClick={() =>
                handleFilterClick({
                  type: "scheduledStatus",
                  header: "Scheduled Status",
                })
              }
              sortOrder={getSortOrder(
                "scheduledStatus",
                sortCriteria,
                sortOrder,
              )}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("scheduledStatus", sortCriteria, sortOrder),
                );
                setSortCriteria("scheduledStatus");
              }}
              isActiveFilter={!!filters?.scheduledStatus?.length}
            />
          ),
        ],
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
      },
    },
    {
      key: "lastUpdated",
      displayName: "Updated",
      header: {
        formatters: [
          (name: ReactNode) => (
            <ColumnHeader
              id="lastUpdated"
              name={name}
              sortOrder={getSortOrder("lastUpdated", sortCriteria, sortOrder)}
              onSortClick={() => {
                setSortOrder(
                  getNewSortOrder("lastUpdated", sortCriteria, sortOrder),
                );
                setSortCriteria("lastUpdated");
              }}
            />
          ),
        ],
      },
      template: (date: string) => {
        if (!date) return "";
        const isWithinLast24Hours = withinLast24Hours(date);
        return isWithinLast24Hours ? (
          <Tooltip
            id="tool-recently-updated"
            text="Case Details have been updated within the last 24 hours. Please check the Case Details for any changes."
          >
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <Icon icon="Time" />
              {formatDate({ date, format: "ff" })}
            </div>
          </Tooltip>
        ) : (
          formatDate({ date, format: "ff" })
        );
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
        props: {
          style: {
            minWidth: "180px",
          },
        },
      },
    },
    {
      key: "caseDetails",
      displayName: "",
      header: {
        formatters: [
          (name: ReactNode) => <ColumnHeader id="details" name={name} />,
        ],
      },
      template: (value: string, { rowData }: RowData<ProcedureTable>) => {
        return (
          <Button
            variant="secondary"
            onClick={() => {
              setDrawerData(rowData);
              handleDrawerToggle();
            }}
          >
            Case Details
          </Button>
        );
      },
      cell: {
        transforms: [handleRecentlyUpdatedHighlight],
        props: {
          style: {
            minWidth: "140px",
          },
        },
      },
    },
  ].filter((column) => !excludedColumns.includes(column.key));

  const handlePatientNameSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!patientName) return;
    updateFilters("patientNames", [patientName]);
  };

  const handlePaginationChange = (newPageNumber: number) => {
    setPageNumber(newPageNumber + 1);
  };

  const handleRecentlyUpdatedClick = () => {
    updateFilters(
      "recentlyUpdated",
      filters?.recentlyUpdated?.length ? [] : ["true"],
    );
  };

  const handleDrawerToggle = () => setOpenDrawer((prev) => !prev);

  return (
    <Wrapper>
      <PaginatorWrapper>
        <RefetchContainer>
          <LastUpdatedContainer data-testid="test-last-updated">
            Last Updated: {formatMillisDate(dataUpdatedAt)}
          </LastUpdatedContainer>
          <Button
            icon="Refresh"
            variant="secondary"
            onClick={refetch}
            data-testid="test-filter-refetch"
          ></Button>
        </RefetchContainer>
        <Spacer />
        <Paginator
          totalPages={totalPages}
          currentPage={pageNumber}
          totalItems={totalItems}
          onPaginationChange={handlePaginationChange}
        />
      </PaginatorWrapper>
      <Tabs
        selectedIndex={tabs.findIndex((tab: TabsType) => {
          return tab?.tabLabel === currentTable;
        })}
        onTabsChange={handleTabChange}
      >
        {tabs.map((task) => (
          <TabPane
            key={task.tabLabel}
            label={`${task.tabLabel} (${task.count})`}
          >
            <SearchInteractionsContainer>
              <SearchWrapper onSubmit={handlePatientNameSubmit}>
                <Input
                  placeholder="Search by patient name"
                  type="text"
                  name="patientName"
                  icon="Search"
                  value={patientName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setPatientName(e.target.value)
                  }
                  data-testid="test-filter-patientName"
                />
              </SearchWrapper>
              <Button
                style={
                  filters?.recentlyUpdated?.length
                    ? {
                        backgroundColor: "#F0F9FF",
                      }
                    : {}
                }
                icon="Time"
                data-testid="test-filter-recently-updated"
                text="Recently Updated"
                variant="secondary"
                onClick={handleRecentlyUpdatedClick}
              />
            </SearchInteractionsContainer>
            {ordersToUpdateStatus.length > 0 && (
              <BulkStatusUpdate
                ordersToUpdate={ordersToUpdateStatus}
                clearOrdersToUpdate={clearAllOrders}
              />
            )}
            <TableWrapper>
              <Table
                rowKey="caseNumber"
                nowrapHeaders
                layout="compact"
                columns={columns}
                rows={rowState}
              />
            </TableWrapper>
          </TabPane>
        ))}
      </Tabs>
      {drawerData && (
        <Drawer
          open={openDrawer}
          handleDrawerToggle={handleDrawerToggle}
          tabs={[
            {
              tabLabel: "Case Details",
              component: <CaseDetails caseId={drawerData.caseNumber} />,
            },
            {
              tabLabel: "Notes",
              component: <Notes caseId={drawerData.caseNumber} />,
            },
          ]}
        />
      )}
      <Modal
        show={modal.show}
        headerText={modal.headerText}
        includeButtonRow={false}
        onHide={() => setModal({ show: false })}
        width={modal.useLarge ? "large" : "small"}
      >
        {modal.renderForm()}
      </Modal>
    </Wrapper>
  );
};

export default OrderTable;
